import React, { useEffect, useMemo, useState } from "react";
import { LoadingSkeleton } from "@narmi/design_system";
import debounce from "lodash.debounce";
import { Search } from "react-feather";
import { useLocalization } from "@fluent/react";
import ApiHttp from "byzantine/src/ApiHttp";
import ThreadList from "./ThreadList";

type ThreadSearchProps = {
  closeSearch: () => void;
  selectedThreadId?: string;
  setSelectedThreadId: (threadId?: string) => void;
};

type MatchingThread = {
  threadId: string;
  subject: string;
  updatedAt: string;
};

type ThreadResponseItem = {
  id: string;
  subject: string;
  updated_at: string;
};

type ThreadSearchResponse = {
  threads: ThreadResponseItem[];
};

const ThreadSearch = ({
  closeSearch,
  selectedThreadId,
  setSelectedThreadId,
}: ThreadSearchProps) => {
  const { l10n } = useLocalization();
  const [subjectFilter, setSubjectFilter] = useState("");
  const [matchingThreads, setMatchingThreads] = useState<
    MatchingThread[] | null
  >(null);
  const [isSearching, setIsSearching] = useState(true);

  const loadThreads = useMemo(
    () =>
      debounce((filter: string) => {
        setIsSearching(true);
        const params = { order_by: "updated_at", filter };

        ApiHttp.fetch("threads", { params }).then(
          (response: ThreadSearchResponse) => {
            setIsSearching(false);
            setMatchingThreads(
              response.threads.map((thread: ThreadResponseItem) => ({
                threadId: thread.id,
                subject: thread.subject,
                updatedAt: thread.updated_at,
              }))
            );
          }
        );
      }, 500),
    []
  );

  useEffect(() => {
    loadThreads(subjectFilter);
  }, [subjectFilter, loadThreads]);

  return (
    <div>
      <div className="search-bar">
        <div className="ui icon input">
          <Search className="message-search-icon" color="#8c8c8c" />
          <input
            type="text"
            className="field"
            value={subjectFilter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSubjectFilter(event.target.value)
            }
            placeholder={l10n.getString(
              "placeholder-search-messages",
              null,
              "Search messages"
            )}
          />
        </div>
        <button
          className="ui button message-search-button"
          onClick={closeSearch}
        >
          {l10n.getString("button-cancel", null, "Cancel")}
        </button>
      </div>
      <LoadingSkeleton content="headerText" isLoading={isSearching} lines={3}>
        <ThreadList
          threads={matchingThreads}
          selectedThreadId={selectedThreadId}
          onThreadSelected={(newSelectedThreadId: string) =>
            setSelectedThreadId(newSelectedThreadId)
          }
          emptyListSubject={l10n.getString(
            "message-search-no-results",
            null,
            "No results found"
          )}
          emptyListSubtext={l10n.getString(
            "message-search-adjust-search",
            null,
            "Please try adjusting your search"
          )}
        />
      </LoadingSkeleton>
    </div>
  );
};

export default ThreadSearch;
